import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, MapPin } from "lucide-react";
import { seoPages } from "@/data/seoPages";
import { villePages } from "@/data/villePages";

const NAV = [
  { label: "Accueil", to: "/" },
  { label: "Services", to: "/#services" },
  { label: "À propos", to: "/#pourquoi" },
  { label: "Zones d'intervention", to: "/#zones" },
  { label: "Contact", to: "/#contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-midnight border-b border-white/10" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link" onClick={() => setOpen(false)}>
          <img src="/logo.png" alt="MSC Toitures — couvreur dans le Val-de-Marne (94)" className="h-10 md:h-12 w-auto" data-testid="logo-image" />
          <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 group-hover:text-signal transition-colors duration-300 mt-1">
            Couvreur 94
          </span>
        </Link>
        <nav className="hidden lg:flex items-center gap-8" data-testid="main-nav">
          {NAV.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                location.pathname + location.hash === item.to ? "text-signal" : "text-white/70 hover:text-white"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href="tel:0646300391"
            data-testid="header-call-button"
            className="btn-sweep hidden md:inline-flex items-center gap-2 bg-signal text-white font-display font-bold text-sm uppercase tracking-wide px-5 py-2.5 transition-colors duration-300"
          >
            <Phone size={15} strokeWidth={1.5} />
            06 46 30 03 91
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-midnight border-t border-white/10"
            data-testid="mobile-nav"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {NAV.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="font-display font-bold text-lg text-white/80 uppercase tracking-tight"
                >
                  {item.label}
                </Link>
              ))}
              <a href="tel:0646300391" className="inline-flex items-center gap-2 text-signal font-mono text-sm tracking-widest pt-2">
                <Phone size={15} strokeWidth={1.5} /> 06 46 30 03 91
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => (
  <footer className="bg-midnight text-white border-t border-white/10 grain" data-testid="site-footer">
    <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
      <div className="md:col-span-4">
        <img src="/logo.png" alt="MSC Toitures — couvreur dans le Val-de-Marne (94)" className="h-16 w-auto" data-testid="footer-logo" />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal mt-3">Couvreur — Val-de-Marne (94)</p>
        <div className="mt-6 space-y-2 text-sm text-white/60">
          <p className="flex items-start gap-2">
            <MapPin size={15} strokeWidth={1.5} className="mt-0.5 shrink-0 text-signal" />
            <span>34 boulevard de Polangis<br />94500 Champigny-sur-Marne</span>
          </p>
          <p>
            <a href="tel:0646300391" data-testid="footer-phone-1" className="hover:text-signal transition-colors duration-300">06 46 30 03 91</a>
            {" — "}
            <a href="tel:0187078662" data-testid="footer-phone-2" className="hover:text-signal transition-colors duration-300">01 87 07 86 62</a>
          </p>
        </div>
      </div>
      <div className="md:col-span-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-5">Navigation</p>
        <ul className="space-y-3 text-sm">
          {NAV.map((item) => (
            <li key={item.label}>
              <Link to={item.to} className="text-white/70 hover:text-signal transition-colors duration-300">{item.label}</Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-5">Nos expertises dans le 94</p>
        <ul className="grid grid-cols-1 gap-y-3 text-sm">
          {seoPages.map((p) => (
            <li key={p.slug}>
              <Link to={`/${p.slug}`} data-testid={`footer-seo-${p.slug}`} className="text-white/70 hover:text-signal transition-colors duration-300">
                {p.footerLabel}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-5">Nos communes</p>
        <ul className="space-y-3 text-sm">
          {villePages.map((p) => (
            <li key={p.slug}>
              <Link to={`/${p.slug}`} data-testid={`footer-ville-${p.slug}`} className="text-white/70 hover:text-signal transition-colors duration-300">
                {p.ville}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
    <div className="relative z-10 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-6 flex flex-col sm:flex-row justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
          © {new Date().getFullYear()} MSC Toitures — Champigny-sur-Marne
        </p>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/30 flex gap-6">
          <Link to="/mentions-legales" data-testid="footer-legal-link" className="hover:text-signal transition-colors duration-300">Mentions légales</Link>
          <Link to="/politique-de-confidentialite" data-testid="footer-privacy-link" className="hover:text-signal transition-colors duration-300">Confidentialité</Link>
        </p>
      </div>
    </div>
  </footer>
);

const MobileCallBar = () => (
  <div className="fixed bottom-0 left-0 w-full z-50 md:hidden grid grid-cols-2 border-t border-white/10 pb-[env(safe-area-inset-bottom)] bg-midnight" data-testid="mobile-call-bar">
    <a
      href="tel:0646300391"
      data-testid="mobile-sticky-call"
      className="flex items-center justify-center gap-2 bg-signal text-white font-display font-bold text-base uppercase tracking-wide py-4 min-h-[52px]"
    >
      <Phone size={16} strokeWidth={1.5} />
      Appeler
    </a>
    <Link
      to="/#contact"
      data-testid="mobile-sticky-quote"
      className="flex items-center justify-center bg-midnight text-white font-display font-bold text-base uppercase tracking-wide py-4 min-h-[52px]"
    >
      Devis gratuit
    </Link>
  </div>
);

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-bone">
      <Header />
      <main className="pt-[72px] pb-[calc(52px+env(safe-area-inset-bottom))] md:pb-0">{children}</main>
      <Footer />
      <MobileCallBar />
    </div>
  );
}
